/**
 * Compound interest calculator with optional monthly contributions.
 */

import type {
  CompoundingFrequency,
  CompoundInput,
  CompoundResult,
  CompoundYearlyBreakdown,
} from '../shared/types';

export const COMPOUNDING_FREQUENCIES: Record<CompoundingFrequency, number> = {
  annually: 1,
  quarterly: 4,
  monthly: 12,
  daily: 365,
};

export function calculateCompoundGrowth({
  principal,
  monthlyContribution,
  annualRate,
  years,
  compoundingFrequency = 'monthly',
}: CompoundInput): CompoundResult {
  const p = Math.max(0, principal);
  const monthly = Math.max(0, monthlyContribution);
  const rate = Math.max(0, annualRate) / 100;
  const totalYears = Math.max(0, Math.floor(years));
  const periodsPerYear = COMPOUNDING_FREQUENCIES[compoundingFrequency] ?? 12;

  const yearlyBreakdown: CompoundYearlyBreakdown[] = [];
  let balance = p;
  let totalContributions = p;

  for (let year = 1; year <= totalYears; year++) {
    const startBalance = balance;
    const startContributions = totalContributions;

    for (let month = 1; month <= 12; month++) {
      balance += monthly;
      totalContributions += monthly;

      const periodRate = rate / periodsPerYear;
      const compoundEvents = Math.floor((month * periodsPerYear) / 12)
        - Math.floor(((month - 1) * periodsPerYear) / 12);

      for (let c = 0; c < compoundEvents; c++) {
        balance *= 1 + periodRate;
      }
    }

    yearlyBreakdown.push({
      year,
      startBalance,
      endBalance: balance,
      contributionsThisYear: totalContributions - startContributions,
      interestThisYear: balance - startBalance - (totalContributions - startContributions),
      totalContributions,
      totalInterest: balance - totalContributions,
    });
  }

  const finalBalance = balance;
  const totalInterest = finalBalance - totalContributions;

  return {
    principal: p,
    monthlyContribution: monthly,
    annualRate,
    years: totalYears,
    compoundingFrequency,
    finalBalance,
    totalContributions,
    totalInterest,
    yearlyBreakdown,
  };
}
